<template>
  <modal :show.sync="show" modalClasses="modal-hide-footer">
    <h3 slot="header" class="mb-0">Legal Cases</h3>
    <div v-if="_gAuth.isAppSas()">
      <!-- Table -->
      <div v-if="tableData.length > 0">
        <el-table :data="tableData" style="width: 100%">
          <el-table-column
            prop="legal_case_case_type"
            label="Case Type"
            min-width="150"
            width="auto"
          >
            <template slot-scope="scope">
              <p class="text-break top-line">
                {{ scope.row.legal_case_case_type }}
              </p>
            </template>
          </el-table-column>

          <el-table-column
            prop="legal_case_plaintiff"
            label="Plaintiff"
            min-width="150"
            width="auto"
          >
            <template slot-scope="scope">
              <p class="text-break top-line">
                {{ scope.row.legal_case_plaintiff || "-" }}
              </p>
            </template>
          </el-table-column>

          <el-table-column label="Solicitor/Tel" min-width="180" width="auto">
            <template slot-scope="scope">
              <p class="mb-0 top-line">
                {{ scope.row.legal_case_solicitor_name || "-" }}
              </p>
              <p class="text-uppercase bottom-line mb-0">
                {{ scope.row.legal_case_solicitor_tel }}
              </p>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <!-- Empty State -->
      <div v-else class="bg-light p-3 text-center rounded-bottom">
        <p class="text-xs">No Legal Cases recorded</p>
      </div>
    </div>
    <div v-else>
      <!-- Summary Table -->
      <h4 style="margin-bottom: 10px">
        D1: LEGAL CASES (SUBJECT AS DEFENDANT)
      </h4>
      <h5 style="margin-top: 20px; margin-bottom: 10px;">SUMMARY</h5>
       <table class="legal-cases-table bordered summary-table" style="width: 100%">
        <thead>
          <tr>
            <th style="width: 60px">No.</th>
            <th>Case Type</th>
            <th>Amount</th>
            <th>Plaintiff</th>
            <th>Status</th>
            <th>Subject Comments</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(legalCase, index) in normalizedLegalCasesSummary"
            :key="'summary-' + index"
          >
            <td style="text-align: center">{{ index + 1 }}.</td>
            <td>{{ safeDisplay(legalCase.case_type) }}</td>
            <td style="text-align: center">
              {{ formatCaseAmount(legalCase) }}
            </td>
            <td>{{ safeDisplay(legalCase.plaintiff) }}</td>
            <td>
              {{ safeDisplay(legalCase.latest_status) }}
            </td>
            <td>
              {{ safeDisplay(legalCase.subject_comment) }}
            </td>
          </tr>
          <tr v-if="normalizedLegalCasesSummary.length === 0">
            <td colspan="6" class="text-center">No Information Available</td>
          </tr>
        </tbody>
      </table>

      <!-- Detailed Legal Cases Data -->
      <div class="detailed-legal-cases" style="margin-top: 30px">
        <h4 style="margin-bottom: 20px">
          D1: LEGAL CASES (SUBJECT AS DEFENDANT)
        </h4>
        <div v-if="normalizedLegalCases.length > 0">
          <div
            v-for="(legalCase, index) in normalizedLegalCases"
            :key="index"
            class="case-detail"
            style="margin-bottom: 30px; page-break-inside: avoid"
          >
            <!-- Case Header -->
            <div
              class="case-header"
              style="
                background: #f5f5f5;
                padding: 10px;
                border-left: 4px solid #ccc;
              "
            >
              <strong>{{ index + 1 }}. {{ safeDisplay(legalCase.title) }}</strong>
            </div>

            <!-- Note -->
            <div
              class="case-note"
              style="
                font-style: italic;
                margin: 10px 0;
                padding: 10px;
                border-radius: 4px;
              "
            >
              Note: The record below is NOT intended to reflect the 'current
              status' of the case. Any judgement, order or action where
              indicated, could have subsequently been settled, quashed,
              rescinded, revoked, set aside, annulled, struck off or dismissed.
              Please check with the relevant parties.
            </div>

            <!-- PROPERTY-RELATED TABLE -->
            <table
              v-if="hasPropertyData(legalCase)"
              class="legal-cases-table bordered"
              style="width: 100%; margin-bottom: 15px"
            >
              <tbody>
                <tr>
                  <td style="width: 150px; background: #e9e9e9">
                    <strong>Special Remark</strong>
                  </td>
                  <td colspan="3">{{ safeJoin(legalCase.special_remark) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Name</strong></td>
                  <td colspan="3">{{ safeDisplay(legalCase.name) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>New ID</strong></td>
                  <td colspan="3">
                    {{ safeDisplay(legalCase.nic_brno) }} ({{
                      legalCase.nic_brno_match === "1"
                        ? "NEW ID MATCH"
                        : "ID MISMATCH"
                    }})
                  </td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Old ID</strong></td>
                  <td colspan="3">{{ safeJoin(legalCase.ic_lcno) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Remark</strong></td>
                  <td colspan="3">{{ safeDisplay(legalCase.remark) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Address</strong></td>
                  <td colspan="3">{{ safeDisplay(legalCase.addr) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Plaintiff</strong></td>
                  <td colspan="3">{{ safeDisplay(legalCase.plaintiff || legalCase.exparte) }}</td>
                </tr>

                <!-- Case No. and Court side by side -->
                <tr>
                  <td style="background: #e9e9e9"><strong>Case No.</strong></td>
                  <td>{{ safeDisplay(legalCase.case_no) }}</td>
                  <td style="background: #e9e9e9"><strong>Court</strong></td>
                  <td>{{ safeDisplay(legalCase.court_detail) }}</td>
                </tr>

                <!-- Originating Summons and Gazette side by side -->
              <tr>
                <td style="background: #e9e9e9">
                  <strong>Originating Summons Dated</strong>
                </td>
                <td>
                  <span v-if="legalCase.originating_summons && legalCase.originating_summons.date && legalCase.originating_summons.date.length > 0">
                    {{ safeDisplay(legalCase.originating_summons.date[0]) }}
                    <span v-if="legalCase.originating_summons.source_detail">
                      {{ safeDisplay(legalCase.originating_summons.source_detail) }}
                    </span>
                  </span>
                  <span v-else-if="legalCase.originating_summons && legalCase.originating_summons.source_detail">
                    {{ safeDisplay(legalCase.originating_summons.source_detail) }}
                  </span>
                  <span v-else>-</span>
                </td>
                <td style="background: #e9e9e9"><strong>Gazette</strong></td>
                <td>{{ safeJoin(legalCase.gazette) }}</td>
              </tr>

                <!-- Outstanding Amount and Hearing Date side by side -->
                <tr>
                  <td style="background: #e9e9e9">
                    <strong>Outstanding Amount</strong>
                  </td>
                  <td>
                    {{
                      legalCase.outstanding_amount
                        ? Number(legalCase.outstanding_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '-'
                    }}
                  </td>
                  <td style="background: #e9e9e9">
                    <strong>Hearing Date</strong>
                  </td>
                  <td>{{ safeDisplay(legalCase.hear_date) }}</td>
                </tr>

                <!-- Order For Sale Date and Reserve Price side by side -->
                <tr>
                  <td style="background: #e9e9e9">
                    <strong>Order For Sale Date</strong>
                  </td>
                  <td>{{ safeDisplay(legalCase.order_for_sale_date) }}</td>
                  <td style="background: #e9e9e9">
                    <strong>Reserve Price</strong>
                  </td>
                  <td>
                    {{
                      legalCase.reserve_price
                        ? Number(legalCase.reserve_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '-'
                    }}
                  </td>
                </tr>

                <!-- Auction Date and Gazette side by side -->
                <tr>
                  <td style="background: #e9e9e9">
                    <strong>Auction Date</strong>
                  </td>
                  <td>
                    <span v-if="legalCase.auction && legalCase.auction.date">
                      {{ safeDisplay(legalCase.auction.date) }}
                      <span v-if="legalCase.auction.source_detail">
                        {{ safeDisplay(legalCase.auction.source_detail) }}</span
                      >
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td style="background: #e9e9e9"><strong>Gazette</strong></td>
                  <td>{{ safeJoin(legalCase.gazette) }}</td>
                </tr>

                <!-- Property Location -->
                <tr>
                  <td style="background: #e9e9e9">
                    <strong>Property Location</strong>
                  </td>
                  <td colspan="3">{{ safeDisplay(legalCase.property_location) }}</td>
                </tr>

                <!-- Property Description -->
                <tr>
                  <td style="background: #e9e9e9">
                    <strong>Property Description</strong>
                  </td>
                  <td colspan="3">{{ safeDisplay(legalCase.property_description) }}</td>
                </tr>

                <!-- Registered Owner -->
                <tr>
                  <td style="background: #e9e9e9">
                    <strong>Registered Owner</strong>
                  </td>
                  <td colspan="3">{{ safeDisplay(legalCase.register_owner) }}</td>
                </tr>

                <!-- Solicitor Information -->
                <tr v-if="legalCase.lawyer && legalCase.lawyer.name">
                  <td style="background: #e9e9e9">
                    <strong>Solicitor</strong>
                  </td>
                  <td colspan="3">{{ safeDisplay(legalCase.lawyer.name) }}</td>
                </tr>
                <tr
                  v-if="
                    legalCase.lawyer &&
                    (legalCase.lawyer.add1 ||
                      legalCase.lawyer.add2 ||
                      legalCase.lawyer.add3)
                  "
                >
                  <td style="background: #e9e9e9"><strong>Address</strong></td>
                  <td colspan="3">
                    {{ safeDisplay(legalCase.lawyer.add1) }}
                    {{ safeDisplay(legalCase.lawyer.add2) }}
                    {{ safeDisplay(legalCase.lawyer.add3) }}
                    {{ safeDisplay(legalCase.lawyer.add4) }}
                  </td>
                </tr>

                <!-- Tel and Ref side by side -->
                <tr
                  v-if="
                    legalCase.lawyer &&
                    (legalCase.lawyer.tel || legalCase.lawyer.ref)
                  "
                >
                  <td style="background: #e9e9e9"><strong>Tel</strong></td>
                  <td>{{ safeDisplay(legalCase.lawyer.tel) }}</td>
                  <td style="background: #e9e9e9"><strong>Ref</strong></td>
                  <td>{{ safeDisplay(legalCase.lawyer.ref) }}</td>
                </tr>
              </tbody>
            </table>

            <!-- REGULAR SUMMONS TABLE -->
            <table
              v-else
              class="legal-cases-table bordered"
              style="width: 100%; margin-bottom: 15px"
            >
              <tbody>
                <tr>
                  <td style="width: 150px; background: #e9e9e9">
                    <strong>Special Remark</strong>
                  </td>
                  <td colspan="3">{{ safeJoin(legalCase.special_remark) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Name</strong></td>
                  <td colspan="3">{{ safeDisplay(legalCase.name) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>New ID</strong></td>
                  <td colspan="3">
                    {{ safeDisplay(legalCase.nic_brno) }} ({{
                      legalCase.nic_brno_match === "1"
                        ? "NEW ID MATCH"
                        : "ID MISMATCH"
                    }})
                  </td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Old ID</strong></td>
                  <td colspan="3">{{ safeJoin(legalCase.ic_lcno) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Remark</strong></td>
                  <td colspan="3">{{ safeDisplay(legalCase.remark) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Address</strong></td>
                  <td colspan="3">{{ safeDisplay(legalCase.addr) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Firm</strong></td>
                  <td colspan="3">{{ safeJoin(legalCase.firm) }}</td>
                </tr>
                <tr>
                  <td style="background: #e9e9e9"><strong>Exparte</strong></td>
                  <td colspan="3">{{ safeDisplay(legalCase.exparte) }}</td>
                </tr>

                <!-- Case No. and Hearing Date side by side -->
                <tr>
                  <td style="background: #e9e9e9"><strong>Case No.</strong></td>
                  <td>{{ safeDisplay(legalCase.case_no) }}</td>
                  <td style="background: #e9e9e9">
                    <strong>Hearing Date</strong>
                  </td>
                  <td>{{ safeDisplay(legalCase.hear_date) }}</td>
                </tr>

                <!-- Notice Date and Gazette side by side -->
                <tr>
                  <td style="background: #e9e9e9">
                    <strong>Notice Date</strong>
                  </td>
                  <td>
                    <span v-if="legalCase.notice && legalCase.notice.date">
                      {{ safeDisplay(legalCase.notice.date) }}
                      <span v-if="legalCase.notice.source_detail">
                        {{ safeDisplay(legalCase.notice.source_detail) }}</span
                      >
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td style="background: #e9e9e9"><strong>Gazette</strong></td>
                  <td>{{ safeJoin(legalCase.gazette_notice) }}</td>
                </tr>

                <!-- Petition Date and Gazette side by side -->
                <tr>
                  <td style="background: #e9e9e9">
                    <strong>Petition Date</strong>
                  </td>
                  <td>
                    <span v-if="legalCase.petition && legalCase.petition.date">
                      {{ safeDisplay(legalCase.petition.date) }}
                      <span v-if="legalCase.petition.source_detail">
                        {{ safeDisplay(legalCase.petition.source_detail) }}</span
                      >
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td style="background: #e9e9e9"><strong>Gazette</strong></td>
                  <td>{{ safeJoin(legalCase.gazette_petition) }}</td>
                </tr>

                <!-- Order Date and Gazette side by side -->
                <tr>
                  <td style="background: #e9e9e9">
                    <strong>Order Date</strong>
                  </td>
                  <td>
                    <span v-if="legalCase.order && legalCase.order.date">
                      {{ safeDisplay(legalCase.order.date) }}
                      <span v-if="legalCase.order.source_detail">
                        {{ safeDisplay(legalCase.order.source_detail) }}</span
                      >
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td style="background: #e9e9e9"><strong>Gazette</strong></td>
                  <td>{{ safeJoin(legalCase.gazette_order) }}</td>
                </tr>

                <!-- Annulment/Disc and Gazette side by side -->
                <tr>
                  <td style="background: #e9e9e9">
                    <strong>Annulment/Disc</strong>
                  </td>
                  <td>{{ safeJoin(legalCase.gazette_discharge) }}</td>
                  <td style="background: #e9e9e9"><strong>Gazette</strong></td>
                  <td>-</td>
                </tr>

                <!-- Amount (full width) -->
                <tr>
                  <td style="background: #e9e9e9"><strong>Amount</strong></td>
                  <td colspan="3">
                    {{
                      legalCase.amount
                        ? Number(legalCase.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '-'
                    }}
                  </td>
                </tr>

                <!-- Solicitor Information -->
                <tr v-if="legalCase.lawyer && legalCase.lawyer.name">
                  <td style="background: #e9e9e9">
                    <strong>Solicitor</strong>
                  </td>
                  <td colspan="3">{{ safeDisplay(legalCase.lawyer.name) }}</td>
                </tr>
                <tr
                  v-if="
                    legalCase.lawyer &&
                    (legalCase.lawyer.add1 ||
                      legalCase.lawyer.add2 ||
                      legalCase.lawyer.add3)
                  "
                >
                  <td style="background: #e9e9e9"><strong>Address</strong></td>
                  <td colspan="3">
                    {{ safeDisplay(legalCase.lawyer.add1) }}
                    {{ safeDisplay(legalCase.lawyer.add2) }}
                    {{ safeDisplay(legalCase.lawyer.add3) }}
                    {{ safeDisplay(legalCase.lawyer.add4) }}
                  </td>
                </tr>

                <!-- Tel and Ref side by side -->
                <tr
                  v-if="
                    legalCase.lawyer &&
                    (legalCase.lawyer.tel || legalCase.lawyer.ref)
                  "
                >
                  <td style="background: #e9e9e9"><strong>Tel</strong></td>
                  <td>{{ safeDisplay(legalCase.lawyer.tel) }}</td>
                  <td style="background: #e9e9e9"><strong>Ref</strong></td>
                  <td>{{ safeDisplay(legalCase.lawyer.ref) }}</td>
                </tr>

                <!-- Exparte Information -->
                <tr v-if="legalCase.cedcon && legalCase.cedcon.name">
                  <td style="background: #e9e9e9">
                    <strong>Exparte</strong>
                  </td>
                  <td colspan="3">{{ safeDisplay(legalCase.cedcon.name) }}</td>
                </tr>
                <tr
                  v-if="
                    legalCase.cedcon &&
                    (legalCase.cedcon.add1 ||
                      legalCase.cedcon.add2 ||
                      legalCase.cedcon.add3)
                  "
                >
                  <td style="background: #e9e9e9"><strong>Contact</strong></td>
                  <td colspan="3">
                    {{ safeDisplay(legalCase.cedcon.add1) }}
                    {{ safeDisplay(legalCase.cedcon.add2) }}
                    {{ safeDisplay(legalCase.cedcon.add3) }}
                    {{ safeDisplay(legalCase.cedcon.add4) }}
                  </td>
                </tr>

                <!-- Tel and Ref side by side -->
                <tr
                  v-if="
                    legalCase.cedcon &&
                    (legalCase.cedcon.tel || legalCase.cedcon.ref)
                  "
                >
                  <td style="background: #e9e9e9"><strong>Tel</strong></td>
                  <td>{{ safeDisplay(legalCase.cedcon.tel) }}</td>
                  <td style="background: #e9e9e9"><strong>Ref</strong></td>
                  <td>{{ safeDisplay(legalCase.cedcon.ref) }}</td>
                </tr>

                <!-- Latest Status Information -->
                <tr v-if="legalCase.latest_status">
                  <td colspan="4" style="background: #f0f0f0; text-align: left">
                    <strong
                      >SUBJECT'S CONFIRMATION OF STATUS AS OF
                      {{ safeDisplayEmptyString(legalCase.latest_status.date) }}.</strong
                    >
                  </td>
                </tr>
                <tr v-if="legalCase.latest_status">
                  <td style="background: #e9e9e9"><strong>Status</strong></td>
                  <td colspan="3">{{ safeDisplay(legalCase.latest_status.code) }}</td>
                </tr>
               <tr v-if="legalCase.latest_status">
                  <td colspan="4" style="background: #e9e9e9; text-align: left">
                    <strong
                      >THIS STATUS WAS UPDATED ON
                      {{ safeDisplayEmptyString(legalCase.latest_status.update_date) }}</strong
                    >
                  </td>
                </tr>
                <tr v-if="legalCase.latest_status">
                  <td style="background: #e9e9e9">
                    <strong>Source Of Update</strong>
                  </td>
                  <td>{{ safeDisplay(legalCase.latest_status.source) }}</td>
                  <td style="background: #e9e9e9">
                    <strong>Source Dated</strong>
                  </td>
                  <td>{{ safeDisplay(legalCase.latest_status.source_date) }}</td>
                </tr>
                <tr v-if="legalCase.latest_status">
                  <td style="background: #e9e9e9">
                    <strong>Next Review Date</strong>
                  </td>
                  <td colspan="3">
                    {{ safeDisplay(legalCase.latest_status.exp_date) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="bg-light p-3 text-center rounded-bottom">
          <p class="text-xs">No Information Available</p>
        </div>
      </div>
    </div>

    <!-- Other sections -->
    <div class="detailed-legal-cases" style="margin-top: 30px">
      <h4 style="margin-bottom: 20px">
        D2: LEGAL CASES (SUBJECT AS PLAINTIFF)
      </h4>
      <div class="bg-light p-3 text-center rounded-bottom">
        <p class="text-xs">No Information Available</p>
      </div>
    </div>
    
    <div class="detailed-legal-cases" style="margin-top: 30px">
      <h4 style="margin-bottom: 20px">D3: AMLA (ANTI MONEY LAUNDERING ACT)</h4>
      <div class="bg-light p-3 text-center rounded-bottom">
        <p class="text-xs">No Information Available</p>
      </div>
    </div>

    <div class="detailed-legal-cases" style="margin-top: 30px">
      <h4 style="margin-bottom: 20px">
        D4: LEGAL CASES (NON PERSONAL CAPACITY)
      </h4>
      <div class="bg-light p-3 text-center rounded-bottom">
        <p class="text-xs">No Information Available</p>
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import { Table, TableColumn } from "element-ui";

export default {
  name: "LegalCasesModal",
  props: {
    legalCases: {
      type: Array,
      default: () => [],
    },
    reservePrice: {
      type: [String, Number],
      default: null,
    },
    legalCasesResp: {
      type: [Array, Object],
      default: () => [],
    },
    legalCasesSummary: {
      type: [Array, Object],
      default: () => [],
    },
    show_legal_cases_full_view: Boolean,
  },
  components: {
    Modal,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  data() {
    return {
      tableData: [],
    };
  },
  computed: {
    show: {
      get() {
        return this.show_legal_cases_full_view;
      },
      set(value) {
        this.$emit("update:show_legal_cases_full_view", value);
      },
    },
    hasLegalCasesData() {
      if (Array.isArray(this.legalCasesResp)) {
        return this.legalCasesResp.length > 0;
      } else if (
        this.legalCasesResp &&
        typeof this.legalCasesResp === "object"
      ) {
        const keys = Object.keys(this.legalCasesResp);
        return (
          keys.length > 1 || (keys.length === 1 && keys[0] !== "@attributes")
        );
      }
      return false;
    },

    normalizedLegalCases() {
      if (Array.isArray(this.legalCasesResp)) {
        return this.legalCasesResp;
      } else if (
        this.legalCasesResp &&
        typeof this.legalCasesResp === "object"
      ) {
        return [this.legalCasesResp];
      }
      return [];
    },
    
    normalizedLegalCasesSummary() {
      if (Array.isArray(this.legalCasesSummary)) {
        return this.legalCasesSummary;
      } else if (
        this.legalCasesSummary &&
        typeof this.legalCasesSummary === "object"
      ) {
        return [this.legalCasesSummary];
      }
      return [];
    },
  },
  methods: {
    // Method to check if it has property-related data
    hasPropertyData(legalCase) {
      return (
        legalCase.property_location ||
        legalCase.property_description ||
        legalCase.register_owner ||
        legalCase.reserve_price ||
        legalCase.order_for_sale_date ||
        legalCase.auction
      );
    },

    formatReservePrice(price) {
      if (!price) return "0";
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    
    // Safe display method to handle empty arrays and other empty values
    safeDisplay(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      
      // Handle empty arrays
      if (Array.isArray(value) && value.length === 0) {
        return '-';
      }
      
      // Handle arrays with content
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      
      // Handle empty objects
      if (typeof value === 'object' && Object.keys(value).length === 0) {
        return '-';
      }
      
      // Handle string representations of empty arrays/objects
      if (typeof value === 'string' && (value === '[]' || value === '{}')) {
        return '-';
      }
      
      return value;
    },
    safeDisplayEmptyString(value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      
      // Handle empty arrays
      if (Array.isArray(value) && value.length === 0) {
        return '';
      }
      
      // Handle empty objects
      if (typeof value === 'object' && Object.keys(value).length === 0) {
        return '';
      }
      
      // Handle string representations of empty arrays/objects
      if (typeof value === 'string' && (value === '[]' || value === '{}')) {
        return '';
      }
      
      return value;
    },
    // Safe join for arrays specifically
    safeJoin(array) {
      if (!array || !Array.isArray(array) || array.length === 0) {
        return "-";
      }
      
      // Filter out empty values before joining
      const filteredArray = array.filter(item => 
        item !== null && 
        item !== undefined && 
        item !== '' && 
        item !== '[]' && 
        item !== '{}'
      );
      
      if (filteredArray.length === 0) {
        return "-";
      }
      
      return filteredArray.join(", ");
    },
    
    // Format case amount - use reserve price for "PROCLAIMATION OF SALE" cases
       formatCaseAmount(legalCase) {      
    // First check if there's an amount in the legalCase object
    if (legalCase.amount && legalCase.amount > 0) {
        return `RM ${this.formatReservePrice(legalCase.amount)}`;
    }
    
    if (legalCase.case_amt && legalCase.case_amt > 0) {
        return `RM ${this.formatReservePrice(legalCase.case_amt)}`;
    }
    
    // Find matching case in legalCasesResp by case type and plaintiff
    if (this.legalCasesResp && Array.isArray(this.legalCasesResp)) {
        const matchingCase = this.legalCasesResp.find(respCase => 
            respCase.title === legalCase.case_type && 
            respCase.plaintiff === legalCase.plaintiff
        );
        
        if (matchingCase && matchingCase.reserve_price && matchingCase.reserve_price > 0) {
            return `RM ${this.formatReservePrice(matchingCase.reserve_price)}`;
        }
    }
    
    return '-';
},
  },
  watch: {
    legalCases: {
      immediate: true,
      handler(val) {
        this.tableData = val && val.length > 0 ? val : [];
      },
    },
  },
};
</script>

<style scoped>
/* Styles for the plain HTML fallback tables */
.legal-cases-table {
  border-collapse: collapse;
  width: 100%;
  font-family: inherit;
}

.legal-cases-table.bordered,
.legal-cases-table th,
.legal-cases-table td {
  border: 1px solid #000 !important;
}

/* Summary table specific header styling */
.summary-table th {
  background-color: #252938 !important;
  color: #fff !important;
  border: none !important;
  vertical-align: top !important;
  text-transform: none !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 19.5px !important;
  padding: 9px 12px !important;
}

/* Regular table header styling (for detailed tables) */
.legal-cases-table:not(.summary-table) th {
  background-color: #a9a9a9 !important;
  background-image: none !important;
  color: #000 !important;
  font-weight: 700 !important;
  text-align: left;
  padding: 8px 6px;
}

.legal-cases-table td {
  background-color: #fff !important;
  color: #000 !important;
  padding: 8px 6px;
  text-align: left;
}

.legal-cases-table td:first-child {
  text-align: left;
}

.legal-cases-table .lc-cell {
  text-align: left;
}

.legal-cases-table .text-break {
  white-space: normal;
}

/* Detailed cases styling */
.detailed-legal-cases {
  margin-top: 30px;
}

.case-detail {
  margin-bottom: 30px;
  page-break-inside: avoid;
}

.case-header {
  background: #f5f5f5;
  padding: 10px;
  border-left: 4px solid #ccc;
  font-size: 16px;
}

.case-note {
  font-style: italic;
  margin: 10px 0;
  padding: 10px;
  background: #c1bfb4;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.3;
}
</style>